import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue"
import SearchInput from "@/components/widgets/common/SearchInput.vue"
import UserAvatar from "@/components/profile/UserAvatar.vue"
import StateChip from "@/components/widgets/common/StateChip.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import { useQuery } from "@vue/apollo-composable"
import { useAuth } from "@/store/auth/useAuth"
import {
  ClientAdminsQuery,
  ClientAdminsQueryQuery,
  ClientAdminsQueryQueryVariables,
  InviteClientAdminMutation,
  InviteClientAdminMutationMutation,
  InviteClientAdminMutationMutationVariables,
  ResendInvitationMutationMutation,
  ResendInvitationMutationMutationVariables,
  ResendInvitationMutation,
  RevokeInvitationMutationMutation,
  RevokeInvitationMutationMutationVariables,
  RevokeInvitationMutation,
  RemoveClientAdminAccessMutationMutation,
  RemoveClientAdminAccessMutationMutationVariables,
  RemoveClientAdminAccessMutation,
  ClientAdminGrant,
  ClientDetailFragmentFragment,
} from "@/gql"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { useSearch } from "@/hooks/useSearch"
import { RefetchQueryDescription } from "apollo-client/core/watchQueryOptions"
import ExpandableTextBlock from "@/components/widgets/common/ExpandableTextBlock.vue"
import { useAccountContext } from "@/hooks/useAccountContext"


export default /*#__PURE__*/_defineComponent({
  __name: 'ClientAdmins',
  props: {
    client: null,
    refetchQueries: null
  },
  setup(__props: any) {

const props = __props as {
  client: ClientDetailFragmentFragment
  refetchQueries?: RefetchQueryDescription
}

const { isStaff } = useAccountContext()


const { user } = useAuth()

const { addSuccess, addError } = useNotification()

const inviteMember = ref(false)
const isSubmitting = ref(false)

const defaultForm = {
  email: "",
}

const form = ref({
  ...defaultForm,
})

const resetForm = () => {
  form.value = {
    ...defaultForm,
  }
}

const observer = ref(null)
const showConfirmDialog = ref(false)
const listAdmins = ref<ClientAdminGrant[]>([])

const invitee = ref({
  email: "",
  prn: "",
})

const inviteActions = {
  RESEND: "resend",
  REVOKE: "revoke",
  REMOVE: "remove",
} as const

const inviteAction = ref<(typeof inviteActions)[keyof typeof inviteActions] | null>(null)

const { mutate } = useMutation()

const { loading, result, onResult } = useQuery<
  ClientAdminsQueryQuery,
  ClientAdminsQueryQueryVariables
>(ClientAdminsQuery, {
  id: props.client.prn,
})

onResult((result) => {
  if (result?.data?.findClient?.clientAdminGrants.length) {
    listAdmins.value = result.data.findClient?.clientAdminGrants as ClientAdminGrant[]
  }
})

const refetchQueries = computed<RefetchQueryDescription>(() => {
  return [
    {
      query: ClientAdminsQuery,
      variables: {
        id: props.client.prn,
      },
    },
    ...(props.refetchQueries || []),
  ]
})

const openInviteMemberModal = () => {
  inviteMember.value = true
}

const onInviteMember = async () => {
  if (!props.client.prn) return
  isSubmitting.value = true

  const result = await mutate<
    InviteClientAdminMutationMutation,
    InviteClientAdminMutationMutationVariables
  >({
    mutation: InviteClientAdminMutation,
    variables: {
      userEmail: form.value.email,
      clientId: props.client.prn,
    },
    handlePayloadErrors: true,
    refetchQueries: refetchQueries.value,
  })

  const error = result?.data && result.data.inviteClientAdmin.error

  if (!error) {
    addSuccess(`Member has been invited`)
    inviteMember.value = false
    resetForm()
  } else {
    addError(error.message)
  }

  isSubmitting.value = false
}

const onSubmitInvitation = async () => {
  const validity = await (observer.value as any)?.validateWithInfo()

  if (!validity.isValid) return
  onInviteMember()
}

const onShowInvitesDialog = (
  action: (typeof inviteActions)[keyof typeof inviteActions],
  data: { email: string; prn: string }
) => {
  inviteAction.value = action
  invitee.value = data
  showConfirmDialog.value = true
}

const confirm = async () => {
  switch (inviteAction.value) {
    case inviteActions.RESEND:
      resendInvite()
      break

    case inviteActions.REVOKE:
      revokeInvite()
      break

    case inviteActions.REMOVE:
      removeAdmin()
      break

    default:
      addError(`Unknown action: ${inviteAction.value}`)
      break
  }
}

const resendInvite = async () => {
  if (!invitee.value.prn) return
  isSubmitting.value = true

  const result = await mutate<
    ResendInvitationMutationMutation,
    ResendInvitationMutationMutationVariables
  >({
    mutation: ResendInvitationMutation,
    variables: {
      id: invitee.value.prn,
    },
    handlePayloadErrors: true,
  })

  if (!result?.data?.resendInvitation.error) {
    addSuccess(`Member has been invited again`)
    showConfirmDialog.value = false
  }
  isSubmitting.value = false
}

const revokeInvite = async () => {
  if (!invitee.value.prn) return
  isSubmitting.value = true

  const result = await mutate<
    RevokeInvitationMutationMutation,
    RevokeInvitationMutationMutationVariables
  >({
    mutation: RevokeInvitationMutation,
    variables: {
      id: invitee.value.prn,
    },
    handlePayloadErrors: true,
    refetchQueries: refetchQueries.value,
  })

  if (!result?.data?.revokeInvitation.error) {
    addSuccess(`Member has been removed`)
    showConfirmDialog.value = false
  }
  isSubmitting.value = false
}

const removeAdmin = async () => {
  if (!invitee.value.prn || !props.client.prn) return
  isSubmitting.value = true

  const result = await mutate<
    RemoveClientAdminAccessMutationMutation,
    RemoveClientAdminAccessMutationMutationVariables
  >({
    mutation: RemoveClientAdminAccessMutation,
    variables: {
      userId: invitee.value.prn,
      clientId: props.client.prn,
    },
    handlePayloadErrors: true,
    refetchQueries: refetchQueries.value,
  })

  if (!result?.data?.removeClientAdminAccess.error) {
    addSuccess(`Member has been removed`)
    showConfirmDialog.value = false
  }
  isSubmitting.value = false
}

const searchKey = ref("")

watch(searchKey, (searchKey) => {
  if (!result?.value?.findClient?.clientAdminGrants.length) return
  if (!searchKey) {
    listAdmins.value = result?.value?.findClient?.clientAdminGrants as ClientAdminGrant[]
  } else {
    listAdmins.value = searchAdmins(searchKey) as ClientAdminGrant[]
  }
})

const searchAdmins = (searchTerm: string) => {
  if (!result?.value?.findClient?.clientAdminGrants.length) return []
  return useSearch({
    items: result?.value?.findClient?.clientAdminGrants,
    search: searchTerm,
    fields: ["client.email", "user.firstName", "user.lastName"],
  })
}

return { __sfc: true,isStaff, props, user, addSuccess, addError, inviteMember, isSubmitting, defaultForm, form, resetForm, observer, showConfirmDialog, listAdmins, invitee, inviteActions, inviteAction, mutate, loading, result, onResult, refetchQueries, openInviteMemberModal, onInviteMember, onSubmitInvitation, onShowInvitesDialog, confirm, resendInvite, revokeInvite, removeAdmin, searchKey, searchAdmins, SearchInput, UserAvatar, StateChip, Icon, ExpandableTextBlock }
}

})